import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { User, Shield, Car, Camera, PenLine, CheckCircle2, History, MapPin, Calculator, Scale, Receipt, ClipboardCheck, ClipboardList, BookText, FileCheck2 } from "lucide-react";

// `desktopOnly: true`        → never in mobile bottom-nav (keeps bar clean).
// `adminOnly: true`          → only Admin/Backoffice see this step (Gutachter sees 7).
// `requiresFinalReport: true`→ Step is only visible when the case has at least
//                              one entry in `final_reports[]` — used for the
//                              new "Gutachten" step where the Gutachter
//                              reviews + forwards the admin-finalized PDF.
// `labelKey` and `mobileLabelKey` are i18n keys under `case_editor.step_labels`
// and `case_editor.steps_short`. The legacy `label`/`mobileLabel` fields are
// kept as fallbacks for any code paths that don't have a `t()` available.
export const STEPS = [
  { key: "overview", label: "Auftrag", labelKey: "case_editor.step_labels.overview", icon: ClipboardList, testid: "bottomnav-overview", desktopOnly: true },
  { key: "customer", label: "Kunde", labelKey: "case_editor.step_labels.customer", icon: User, testid: "bottomnav-customer" },
  { key: "insurance", label: "Vers.", labelKey: "case_editor.step_labels.insurance", mobileLabelKey: "case_editor.steps_short.vers", icon: Shield, testid: "bottomnav-insurance" },
  { key: "vehicle", label: "Fzg.", labelKey: "case_editor.step_labels.vehicle", mobileLabelKey: "case_editor.steps_short.fzg", icon: Car, testid: "bottomnav-vehicle" },
  { key: "begutachtung", label: "Begutachtung", labelKey: "case_editor.step_labels.begutachtung", icon: ClipboardCheck, testid: "sidebar-step-begutachtung", desktopOnly: true, adminOnly: true },
  { key: "inspection", label: "Besichtigung", labelKey: "case_editor.step_labels.inspection", icon: MapPin, testid: "sidebar-step-inspection", desktopOnly: true, adminOnly: true },
  { key: "photos", label: "Fotos", labelKey: "case_editor.step_labels.photos", icon: Camera, testid: "bottomnav-photos", featured: true },
  { key: "signature", label: "Sig.", labelKey: "case_editor.step_labels.signature", mobileLabelKey: "case_editor.steps_short.sig", icon: PenLine, testid: "bottomnav-signature" },
  { key: "predamages", label: "Vorsch.", labelKey: "case_editor.step_labels.predamages", mobileLabel: "Vorsch.", mobileLabelKey: "case_editor.steps_short.vorsch", icon: History, testid: "bottomnav-predamages" },
  { key: "calculation", label: "Kalkulation", labelKey: "case_editor.step_labels.calculation", icon: Calculator, testid: "sidebar-step-calculation", desktopOnly: true, adminOnly: true },
  { key: "valuation", label: "Bewertung", labelKey: "case_editor.step_labels.valuation", icon: Scale, testid: "sidebar-step-valuation", desktopOnly: true, adminOnly: true },
  { key: "honorar", label: "Honorar", labelKey: "case_editor.step_labels.honorar", icon: Receipt, testid: "sidebar-step-honorar", desktopOnly: true, adminOnly: true },
  { key: "documentation", label: "Dokumentation", labelKey: "case_editor.step_labels.documentation", mobileLabel: "Dok.", mobileLabelKey: "case_editor.steps_short.dok", icon: BookText, testid: "sidebar-step-documentation", desktopOnly: true, adminOnly: true },
  { key: "review", label: "Abschluss", labelKey: "case_editor.step_labels.review", mobileLabel: "Absch.", mobileLabelKey: "case_editor.steps_short.absch", icon: CheckCircle2, testid: "bottomnav-review" },
  { key: "gutachten", label: "Gutachten", labelKey: "case_editor.step_labels.gutachten", mobileLabel: "Gut.", mobileLabelKey: "case_editor.steps_short.gut", icon: FileCheck2, testid: "bottomnav-gutachten", requiresFinalReport: true, desktopOnly: true },
];

// Admin-spezifische Reihenfolge (Desktop-Sidebar). Mobile-Bar bleibt unverändert,
// da die meisten Admin-Aufgaben am Desktop erledigt werden.
//
//   1 Auftrag       →  overview
//   2 Kunde         →  customer
//   3 Versicherung  →  insurance
//   4 Unterschrift  →  signature
//   5 Besichtigung  →  inspection
//   6 Fahrzeug      →  vehicle
//   7 Begutachtung  →  begutachtung
//   8 Fotos         →  photos
//   9 Kalkulation   →  calculation
//  10 Bewertung     →  valuation
//  11 Honorar       →  honorar
//  12 Dokumentation →  documentation  (=fertiges editierbares Gutachten 1:1)
//  13 Abschluss     →  review
//
// Hinweis: „Vorschäden" wird im Admin-Workflow ausgeblendet — die Erfassung
// ist Aufgabe des Gutachters.
const _byKey = Object.fromEntries(STEPS.map((s) => [s.key, s]));
const _adminOrder = [
  "overview", "customer", "insurance", "signature", "inspection", "vehicle",
  "begutachtung", "photos", "calculation", "valuation", "honorar",
  "documentation", "review", "gutachten",
];
export const STEPS_ADMIN = _adminOrder.map((k, i) => ({
  ..._byKey[k],
  stepNo: i + 1,
}));

// Mobile bottom-nav only renders entries without `desktopOnly`.
const MOBILE_STEPS = STEPS.filter((s) => !s.desktopOnly);

export default function BottomNav({ current, onChange, completeness = {} }) {
  const { t } = useTranslation();
  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-zinc-200 safe-bottom"
      data-testid="case-bottom-nav"
    >
      <div className="grid grid-cols-7 items-end px-1 pt-1 pb-1">
        {MOBILE_STEPS.map((s) => {
          const Icon = s.icon;
          const isActive = current === s.key;
          const done = completeness[s.key];
          // Mobile-Label: kurz (z.B. "Vers." statt "Versicherung"). Fallback
          // ordering: i18n key → original mobileLabel → label.
          const navLabel = s.mobileLabelKey
            ? t(s.mobileLabelKey)
            : s.mobileLabel || (s.labelKey ? t(s.labelKey) : s.label);
          // Aria-Label nutzt die volle (lange) Bezeichnung für Screen-Reader.
          const ariaLabel = s.labelKey ? t(s.labelKey) : s.label;

          if (s.featured) {
            return (
              <div key={s.key} className="flex justify-center relative">
                {/* Elevated gradient circle for Fotos. Wenn Fotos bereits
                    hochgeladen sind, bekommt der Button einen blauen
                    Außen-Ring als „Erledigt"-Marker, damit der User
                    sofort sieht: hier muss nichts mehr gemacht werden. */}
                <button
                  onClick={() => onChange(s.key)}
                  data-testid={s.testid}
                  aria-label={ariaLabel}
                  className={cn(
                    "relative -mt-10 h-14 w-14 rounded-full brand-gradient-bg text-white",
                    "flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(18,169,232,0.55)]",
                    "border-4 border-white transition-all active:scale-95",
                    isActive && "ring-2 ring-[#12A9E8]/60",
                    !isActive && done && "ring-[3px] ring-blue-400 shadow-[0_8px_24px_-6px_rgba(59,130,246,0.55)]"
                  )}
                >
                  <Icon className="h-6 w-6" strokeWidth={1.9} />
                </button>
              </div>
            );
          }

          return (
            <button
              key={s.key}
              onClick={() => onChange(s.key)}
              data-testid={s.testid}
              aria-label={ariaLabel}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 py-1 rounded-lg transition",
                isActive
                  ? "text-[#12A9E8]"
                  : done
                  ? "text-blue-700 bg-blue-50"
                  : "text-zinc-500 hover:text-zinc-900"
              )}
            >
              <div
                className={cn(
                  "h-7 w-7 rounded-md flex items-center justify-center transition",
                  isActive
                    ? "bg-[#12A9E8]/10 ring-1 ring-[#12A9E8]/30"
                    : done
                    ? "bg-blue-100 ring-1 ring-blue-200"
                    : "bg-transparent"
                )}
              >
                <Icon className="h-[16px] w-[16px]" strokeWidth={done || isActive ? 2 : 1.8} />
              </div>
              <span
                className={cn(
                  "text-[9px] font-semibold leading-none truncate max-w-full px-0.5",
                  isActive ? "text-[#12A9E8]" : done ? "text-blue-700" : "font-medium"
                )}
              >
                {navLabel}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
